import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  MessageSquare,
  Calendar,
  Mail,
  Building,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserSupportRequests = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    fetchMyTickets();
  }, []);

  const fetchMyTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/support/my-tickets`);
      setTickets(response.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load your support requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (ticketId) => {
    if (!reply.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setSendingReply(true);
      await axios.post(`${API}/support/tickets/${ticketId}/reply`, {
        message: reply
      });
      
      // Add reply to local state
      const newUpdate = {
        timestamp: new Date().toISOString(),
        message: reply,
        author: 'user'
      };
      
      setSelectedTicket(prev => ({
        ...prev,
        customer_visible_updates: [...(prev.customer_visible_updates || []), newUpdate],
        status: 'open',
        last_updated_at: new Date().toISOString()
      }));
      
      setTickets(prev => prev.map(ticket => 
        ticket.id === ticketId 
          ? { ...ticket, status: 'open', last_updated_at: new Date().toISOString() }
          : ticket
      ));
      
      setReply('');
      toast.success('Reply sent successfully');
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-red-100', text: 'text-red-800', label: 'Open', icon: AlertCircle },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Awaiting Response', icon: Clock },
      resolved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Resolved', icon: CheckCircle },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed', icon: CheckCircle }
    };
    return badges[status] || badges.open;
  };

  const formatJobCode = (id) => {
    return `JOP-${id?.slice(0, 8).toUpperCase()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <MessageSquare className="h-6 w-6 mr-2" />
                  My Support Requests
                </h1>
                <p className="text-sm text-gray-600">View and respond to your support tickets</p>
              </div>
            </div>
            
            <button
              onClick={fetchMyTickets}
              className="jop-button-secondary flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!selectedTicket ? (
          // Tickets List View
          <div className="jop-card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Your Support Requests</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Response
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Linked Job
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tickets.map((ticket) => {
                    const statusInfo = getStatusBadge(ticket.status);
                    const StatusIcon = statusInfo.icon;
                    
                    return (
                      <tr key={ticket.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(ticket.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                            {ticket.subject}
                          </div>
                          <div className="text-xs text-gray-500">
                            #{ticket.id.slice(0, 8)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.bg} ${statusInfo.text} flex items-center w-fit`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(ticket.last_updated_at).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {ticket.customer_visible_updates?.length ? 
                              `${ticket.customer_visible_updates.length} replies` : 
                              'No replies yet'
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {ticket.job_id ? (
                            <button
                              onClick={() => navigate(`/jobs/${ticket.job_id}`)}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                            >
                              {formatJobCode(ticket.job_id)}
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => setSelectedTicket(ticket)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {tickets.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No support requests yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    When you contact support, your requests will appear here.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Ticket Detail View
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Ticket Header */}
              <div className="jop-card p-6 mb-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      {selectedTicket.subject}
                    </h2>
                    <div className="text-sm text-gray-600">
                      Ticket #{selectedTicket.id.slice(0, 8)} • Created {new Date(selectedTicket.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="jop-button-secondary"
                  >
                    Back to List
                  </button>
                </div>
                
                {/* Original Message */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Original Message:</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.message}</p>
                </div>

                {/* Conversation */}
                <div className="space-y-4 mb-6">
                  {selectedTicket.customer_visible_updates?.map((update, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg ${
                        update.author === 'admin' 
                          ? 'bg-blue-50 border-l-4 border-blue-500' 
                          : 'bg-green-50 border-l-4 border-green-500'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          {update.author === 'admin' ? 'JOP Support' : 'You'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(update.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{update.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                {selectedTicket.status !== 'closed' && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Reply to Support</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Reply
                        </label>
                        <textarea
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          rows="4"
                          className="jop-input resize-none"
                          placeholder="Type your reply here..."
                        />
                      </div>
                      
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleSendReply(selectedTicket.id)}
                          disabled={sendingReply || !reply.trim()}
                          className="jop-button flex items-center"
                        >
                          {sendingReply ? (
                            <div className="spinner" />
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Send Reply
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {selectedTicket.status === 'closed' && (
                  <div className="border-t pt-6">
                    <div className="bg-gray-100 rounded-lg p-4 text-center">
                      <CheckCircle className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-gray-600">This ticket has been closed and is no longer accepting replies.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Ticket Info */}
              <div className="jop-card p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ticket Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Status</span>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${getStatusBadge(selectedTicket.status).bg} ${getStatusBadge(selectedTicket.status).text}`}>
                      {getStatusBadge(selectedTicket.status).label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Priority</span>
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {selectedTicket.priority}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Created</span>
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(selectedTicket.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Last Updated</span>
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(selectedTicket.last_updated_at).toLocaleDateString()}
                    </span>
                  </div>
                  {selectedTicket.job_id && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Related Job</span>
                      <button
                        onClick={() => navigate(`/jobs/${selectedTicket.job_id}`)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        {formatJobCode(selectedTicket.job_id)}
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSupportRequests;